import { DestroyRef, Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
  None = 4,
}

export interface LogEntry<T = unknown> {
  timestamp: Date;
  level: LogLevel;
  message: string;
  context?: string;
  data?: T;
}

export interface RemoteLogPayload {
  timestamp: string;
  level: 'Debug' | 'Info' | 'Warn' | 'Error';
  message: string;
  context?: string;
  data?: unknown;
  userAgent?: string;
  url?: string;
  userId?: string;
  sessionId?: string;
}

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly logLevel = this.getLogLevel();
  private readonly logs: LogEntry[] = [];
  private readonly maxLogs = 1000;
  private readonly destroyRef = inject(DestroyRef);
  private readonly sessionId = this.generateSessionId();
  private pendingLogs: RemoteLogPayload[] = [];
  private readonly batchSize = 5;
  private readonly batchIntervalMs = 5000;
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private logCounter = 0;

  constructor() {
    if (this.isBrowser) {
      this.startBatchTimer();

      // Stop the batch timer on teardown and flush whatever is queued, so the last
      // batch is not silently dropped.
      this.destroyRef.onDestroy(() => {
        if (this.batchTimer !== null) {
          clearInterval(this.batchTimer);
          this.batchTimer = null;
        }
        void this.flushToRemote();
      });

      // A backgrounded or closing tab never reaches onDestroy, so flush there too.
      // 'pagehide' is the reliable signal on mobile Safari, where 'unload' may not fire.
      const flushOnHide = () => {
        if (document.visibilityState === 'hidden') void this.flushToRemote();
      };
      document.addEventListener('visibilitychange', flushOnHide);
      window.addEventListener('pagehide', flushOnHide);

      this.destroyRef.onDestroy(() => {
        document.removeEventListener('visibilitychange', flushOnHide);
        window.removeEventListener('pagehide', flushOnHide);
      });
    }
  }

  private getLogLevel(): LogLevel {
    if (typeof window !== 'undefined' && (window as unknown as { ngDevMode?: boolean }).ngDevMode === false) {
      return LogLevel.Warn;
    }
    return LogLevel.Debug;
  }

  debug<T>(message: string, context?: string, data?: T): void {
    this.log<T>(LogLevel.Debug, message, context, data);
  }

  info<T>(message: string, context?: string, data?: T): void {
    this.log<T>(LogLevel.Info, message, context, data);
  }

  warn<T>(message: string, context?: string, data?: T): void {
    this.log<T>(LogLevel.Warn, message, context, data);
  }

  error<T>(message: string, context?: string, data?: T): void {
    this.log<T>(LogLevel.Error, message, context, data);
  }

  private log<T>(level: LogLevel, message: string, context?: string, data?: T): void {
    if (level < this.logLevel) return;

    const entry: LogEntry<T> = {
      timestamp: new Date(),
      level,
      message,
      context,
      data: this.sanitizeData(data),
    };

    this.logs.push(entry as LogEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // No console output by design - the app ships with a silent console in every
    // environment. Entries remain queryable in-memory via getLogs(), and errors and
    // warnings are still forwarded to the remote sink below, so nothing is lost.

    // Send to remote for errors and warnings
    if ((level === LogLevel.Error || level === LogLevel.Warn) && this.isBrowser) {
      this.queueForRemote(entry);
    }
  }

  private queueForRemote(entry: LogEntry<unknown>): void {
    const payload: RemoteLogPayload = {
      timestamp: entry.timestamp.toISOString(),
      level: LogLevel[entry.level] as RemoteLogPayload['level'],
      message: entry.message,
      context: entry.context,
      data: entry.data,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      sessionId: this.sessionId,
    };

    this.pendingLogs.push(payload);
    this.logCounter++;

    // Send immediately for errors, batch for warnings
    if (entry.level === LogLevel.Error || this.pendingLogs.length >= this.batchSize) {
      this.flushToRemote();
    }
  }

  private startBatchTimer(): void {
    if (!this.isBrowser) return;

    this.batchTimer = setInterval(() => {
      if (this.pendingLogs.length > 0) {
        this.flushToRemote();
      }
    }, this.batchIntervalMs);
  }

  private async flushToRemote(): Promise<void> {
    if (this.pendingLogs.length === 0 || !this.isBrowser) return;

    const logsToSend = [...this.pendingLogs];
    this.pendingLogs = [];

    try {
      // Use fetch API directly to avoid Angular dependency issues during bootstrap
      const apiUrl = environment.apiUrl.replace(/\/api\/?$/i, '');
      await fetch(`${apiUrl}/api/logs/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: logsToSend }),
        // Don't await - fire and forget
      }).catch(() => {
        // Silently fail - logging should not break the app
      });
    } catch {
      // Silently fail
    }
  }

  private sanitizeData<T>(data?: T): T | undefined {
    if (!data || typeof data !== 'object') return data;

    const sensitiveFields = [
      'password', 'passwd', 'pwd',
      'token', 'accesstoken', 'refreshtoken', 'idtoken',
      'secret', 'key', 'apikey', 'api_key',
      'auth', 'authorization', 'bearer',
      'credential', 'ssn', 'socialsecurity',
      'credit', 'card', 'cvv', 'cvc', 'pin',
      'cookie', 'session', 'sessionid',
      'x-api-key', 'api-key',
    ];

    const sanitize = (obj: unknown): unknown => {
      if (obj === null || obj === undefined) return obj;
      if (Array.isArray(obj)) return obj.map(sanitize);
      if (typeof obj === 'object') {
        const sanitized: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
          const lowerKey = key.toLowerCase();
          if (sensitiveFields.some((field) => lowerKey.includes(field.toLowerCase()))) {
            sanitized[key] = '[REDACTED]';
          } else if (typeof value === 'object' && value !== null) {
            sanitized[key] = sanitize(value);
          } else {
            sanitized[key] = value;
          }
        }
        return sanitized;
      }
      return obj;
    };

    return sanitize(data) as T;
  }

  private generateSessionId(): string {
    return `s_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  getLogs(): ReadonlyArray<LogEntry> {
    return this.logs as ReadonlyArray<LogEntry>;
  }

  clearLogs(): void {
    this.logs.length = 0;
  }

  /**
   * Get a summary of recent errors for debugging
   */
  getErrorSummary(): { count: number; messages: string[] } {
    const errors = this.logs.filter(l => l.level === LogLevel.Error);
    return {
      count: errors.length,
      messages: errors.slice(-10).map(e => `${e.timestamp.toISOString()}: ${e.context || 'Unknown'}: ${e.message}`)
    };
  }

  /**
   * Log a caught error with stack trace formatting
   */
  logError(error: unknown, context?: string): void {
    let message = 'Unknown error';
    let data: unknown = undefined;

    if (error instanceof Error) {
      message = error.message;
      data = {
        name: error.name,
        stack: error.stack,
        message: error.message,
      };
    } else if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      message = (error as Record<string, unknown>)['message'] as string || JSON.stringify(error);
      data = error;
    }

    this.error(message, context, data);
  }
}
